//* Importing React and other modules
import React, { useState } from "react";

//* Importing the lucide-react icons
import { LogOut } from "lucide-react";

//* Importing the images
import logo from "../../../assets/images/logo-ontest.png";

//* Importing the styles
import SidebarHeader from "./SidebarHeader";

//* Importing the SidebarItem component
import SidebarItem from "../SideBar/SidebarItem";

//* Importing the styles
import styles from "./style.module.css";

//* Importing the IconMapper
import { getIcon } from "../../common/IconMapper"; 

//* Importing the react-icons
import { MdMenu } from "react-icons/md"; 

//* Importing custom logout hook
import { useLogout } from "../../../hooks/useLogout";

/**
 * SideBar component renders a sidebar with dynamic menu items based on user permissions.
 * It includes a header, dynamically generated menu items, and a footer with a logout button.
 *
 * @component
 * @returns {JSX.Element} The rendered sidebar component.
 *
 * @example
 * // Example usage of SideBar component
 * import SideBar from './SideBar';
 * 
 * function App() {
 *   return (
 *     <div className="App">
 *       <SideBar />
 *     </div>
 *   );
 * }
 *
 * @description
 * The SideBar component uses the following hooks:
 * - `useSelector` to access the `me` object from the Redux store.
 * - `useDispatch` to dispatch actions to the Redux store.
 * - `useNavigate` to navigate to different routes.
 * - `useState` to manage the `shrunk` state for toggling the sidebar width.
 *
 * The component renders:
 * - A header with a toggle button to shrink/expand the sidebar.
 * - A dynamically generated menu based on user permissions.
 * - A footer with a logo and a logout button.
 *
 * @requires
 * - `react`
 * - `react-redux`
 * - `react-router-dom`
 * - `styles` for CSS styling.
 * - `getIcon` function to get the icon component for a feature or subFeature.
 * - `SidebarHeader` component for the sidebar header.
 * - `SidebarItem` component for individual menu items.
 * - `logout` action to log out the user.
 * - `logo` for the footer logo image.
 * - `LogOut` icon for the logout button.
 */
export default function SideBar() {
  //const { me } = useSelector((store) => store.auth);
  const logout = useLogout();

  const [shrunk, setShrunk] = useState(false);

  const handleShrunk = () => {
    setShrunk(!shrunk);
  };
  
  const handleLogout = () => {
    logout();
  };

  return (
    <div
      className={`${styles.sidebar} d-flex flex-column align-items-center hidethis`}
      style={{
        width: shrunk ? "7.2rem" : "20rem",
        transition: "width 0.3s ease-in-out",
        zIndex: 2,
      }}
    >
      <SidebarHeader shrunk={shrunk} handleShrunk={handleShrunk} />

      <hr className={styles.separator} />

      <div className={styles.menuContainer}>
        <SidebarItem
          icon={React.createElement(getIcon("MdDashboard") || MdMenu)}
          title="Dashboard"
          href="/"
          shrunk={shrunk}
        />

        <SidebarItem
          icon={React.createElement(getIcon("MdSchool") || MdMenu)}
          title="Processos Seletivos"
          href="/process"
          shrunk={shrunk}
        />


        {/* Gerar o menu dinamicamente com base nas permissões */}
        {/* {me.permissions &&
          me.permissions.map((feature) => {
            // Exibir apenas features que possuem pelo menos uma subFeature habilitada
            const enabledSubFeatures = feature.subFeatures.filter(
              (subFeature) => subFeature.enabled
            );

            if (enabledSubFeatures.length === 0) {
              return null;
            }

            // Obter o componente do ícone para a feature
            const FeatureIcon = getIcon(feature.icon) || MdMenu; // Ícone padrão

            return (
              <SidebarItem
                key={feature.id}
                icon={React.createElement(FeatureIcon)}
                title={feature.name}
                shrunk={shrunk}
              >
                {enabledSubFeatures.map((subFeature) => {
                  // Obter o componente do ícone para a subFeature
                  const SubFeatureIcon = getIcon(subFeature.icon) || MdMenu; // Ícone padrão

                  return (
                    <SidebarItem
                      key={subFeature.id}
                      icon={React.createElement(SubFeatureIcon)}
                      title={subFeature.name}
                      href={subFeature.path}
                      shrunk={shrunk}
                    />
                  );
                })}
              </SidebarItem>
            );
          })} */}
      </div>

      <hr className={styles.separator} />

      {/* Footer */}
      <div className={`${styles.footer} d-flex flex-column align-items-center`}>
        <img src={logo} alt="Logo" className={styles.logo} />
        <span className={styles.copyright}>
          &copy; {new Date().getFullYear()}
          <span className={styles.logoutContainer}>
            <button
              onClick={handleLogout}
              className={`btn btn-danger btn-sm ${styles.logoutButton}`}
            >
              <LogOut size={16} /> {!shrunk && "Logout"}
            </button>
          </span>
        </span>
      </div>
    </div>
  );
}
